import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Calendar, Clock, MapPin, Pill, CalendarPlus, MessageSquareDot } from "lucide-react";

export default function MobileEventDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [showReschedule, setShowReschedule] = useState(false);
  const [newDate, setNewDate] = useState("");
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.get("/me/calendar").then((r) => {
      for (const c of (r.data.calendars || [])) {
        const e = c.events.find((x) => x.id === id);
        if (e) { setEvent({ ...e, regimen_name: c.regimen_name }); break; }
      }
    });
  }, [id]);

  const submitReschedule = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reschedule-requests", { event_id: id, new_date: newDate, reason });
      setMsg("Request sent. You'll be notified when your provider responds.");
      setShowReschedule(false);
      setNewDate(""); setReason("");
    } catch (e) {
      alert(e.response?.data?.detail || "Failed");
    }
  };

  if (!event) return <div className="max-w-md mx-auto p-6 text-stone-500">Loading…</div>;

  return (
    <div className="max-w-md mx-auto pb-6" data-testid="mobile-event-detail">
      <header className="hero-gradient px-5 pt-6 pb-4 rounded-b-3xl">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-brand-100 text-sm" data-testid="event-back">
          <ArrowLeft className="w-4 h-4" />Back
        </button>
        <div className="mt-3">
          <div className="text-xs text-brand-200 uppercase tracking-wider">{event.event_type}</div>
          <h1 className="font-display font-bold text-2xl mt-1">{event.name}</h1>
        </div>
      </header>

      <section className="px-5 mt-5">
        <div className="bg-white border border-stone-200 rounded-2xl p-5 space-y-3">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-brand-500" />
            <div>
              <div className="font-semibold text-stone-900">{new Date(event.date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}</div>
              {event.time && <div className="text-sm text-stone-500 inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{event.time}</div>}
            </div>
          </div>
          {event.location && (
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-brand-500" />
              <div className="font-medium text-stone-800">{event.location}</div>
            </div>
          )}
          {event.dose && (
            <div className="flex items-center gap-3">
              <Pill className="w-5 h-5 text-brand-500" />
              <div className="text-stone-800">{event.dose} · {event.route}</div>
            </div>
          )}
          {event.cycle_label && (
            <div className="text-sm text-stone-500 pt-2 border-t border-stone-100">Part of <b className="text-stone-800">{event.regimen_name}</b> · {event.cycle_label} (Day {event.cycle_day})</div>
          )}
        </div>
      </section>

      {msg && (
        <div className="mx-5 mt-4 px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm" data-testid="resched-success">{msg}</div>
      )}

      <section className="px-5 mt-4 grid grid-cols-1 gap-3">
        <button onClick={() => setShowReschedule(true)} className="bg-white border border-stone-200 rounded-xl px-4 py-3 text-left flex items-center gap-3 hover:shadow-soft" data-testid="request-reschedule-btn">
          <div className="w-10 h-10 rounded-lg bg-warmth-100 text-warmth-500 flex items-center justify-center"><CalendarPlus className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-semibold text-stone-900">Request reschedule</div>
            <div className="text-xs text-stone-500">Ask your provider to move this appointment.</div>
          </div>
        </button>
        <button onClick={() => nav("/m/messages")} className="bg-white border border-stone-200 rounded-xl px-4 py-3 text-left flex items-center gap-3 hover:shadow-soft" data-testid="contact-provider-btn">
          <div className="w-10 h-10 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center"><MessageSquareDot className="w-5 h-5" /></div>
          <div className="flex-1">
            <div className="font-semibold text-stone-900">Message provider</div>
            <div className="text-xs text-stone-500">Have a question? Send a note.</div>
          </div>
        </button>
      </section>

      {showReschedule && (
        <div className="fixed inset-0 z-50 bg-stone-900/40 backdrop-blur-sm flex items-end justify-center sm:items-center p-4 animate-fade-in" data-testid="reschedule-modal">
          <div className="bg-white rounded-2xl shadow-elevated w-full max-w-md border border-stone-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-stone-100">
              <h3 className="font-display font-semibold text-stone-900">Request a reschedule</h3>
              <p className="text-xs text-stone-500 mt-0.5">Your provider will review and respond.</p>
            </div>
            <form onSubmit={submitReschedule} className="p-5 space-y-3">
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">New preferred date*</label>
                <input data-testid="resched-new-date" required type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
              </div>
              <div>
                <label className="text-sm font-medium text-stone-700 mb-1.5 block">Reason (optional)</label>
                <textarea data-testid="resched-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full px-3 py-2.5 rounded-lg border border-stone-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 focus:outline-none" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowReschedule(false)} className="px-3 py-2 rounded-lg border border-stone-300 text-stone-700 text-sm font-medium">Cancel</button>
                <button data-testid="resched-submit" type="submit" className="px-4 py-2 rounded-lg bg-brand-500 hover:bg-brand-700 text-white text-sm font-semibold">Send request</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
